import { Component, Input, Output, EventEmitter, forwardRef, HostListener, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'app-search-create-dropdown',
    templateUrl: './search-create-dropdown.component.html',
    styleUrls: ['./search-create-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchCreateDropdownComponent),
            multi: true
        }
    ]
})
export class SearchCreateDropdownComponent implements ControlValueAccessor, OnChanges {
    // --- Configuration Inputs ---
    @Input() items: any[] = [];
    @Input() displayField: string = 'name';
    @Input() valueField?: string;
    @Input() placeholder: string = '';
    @Input() isLoading: boolean = false;
    @Input() allowCreateNew: boolean = false;
    @Input() createNewTextFn: (inputValue: string) => string;
    @Input() minCharsForSearch: number = 0;
    @Input() hasError: boolean = false;

    // --- Component Outputs ---
    @Output() itemSelected = new EventEmitter<any>();
    @Output() createNewRequested = new EventEmitter<string>();
    @Output() inputValueChanged = new EventEmitter<string>();
    @Output() focusEvent = new EventEmitter<string>();

    // --- Internal State ---
    inputText: string = '';
    filteredItems: any[] = [];
    showResults: boolean = false;
    private wasActionTaken: boolean = false;

    // --- Value Management ---
    private _lastCommittedValue: any = null;
    private _lastCommittedDisplayText: string = '';

    // --- ControlValueAccessor Implementation ---
    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(private elementRef: ElementRef, private translate: TranslateService) {
        this.createNewTextFn = (inputValue: string) =>
            this.translate.instant('dropdown.create_new', { value: inputValue });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            // When items arrive/change from the outside (e.g. async data load), only
            // update the display text if the user is NOT actively typing.
            const userIsTyping = this.inputText !== this._lastCommittedDisplayText;

            if (!userIsTyping && this._lastCommittedValue) {
                const itemToSelect = this.findItemForValue(this._lastCommittedValue);
                if (itemToSelect) {
                    this.inputText = this.getItemDisplay(itemToSelect);
                    this._lastCommittedDisplayText = this.inputText;
                } else if (typeof this._lastCommittedValue === 'object' && this._lastCommittedValue && this.displayField in this._lastCommittedValue) {
                    this.inputText = this.getItemDisplay(this._lastCommittedValue);
                    this._lastCommittedDisplayText = this.inputText;
                }
            }

            // Re-apply the active search term to the new items set so in-progress
            // filtering is never lost when items are refreshed from the outside.
            const term = this.normalizeString(this.inputText);
            this.filteredItems = term
                ? this.items.filter(item =>
                    this.normalizeString(this.getItemDisplay(item)).includes(term)
                  )
                : [...this.items];
        }
    }

    private findItemForValue(value: any): any | null {
        if (!value) return null;
        return this.valueField
            ? this.items.find(item => item[this.valueField!] === value)
            : this.items.find(item => {
                if (item === value) return true;
                if (typeof value === 'object' && value && typeof item === 'object' && item) {
                    return this.getItemDisplay(item) === this.getItemDisplay(value);
                }
                return false;
            });
    }

    writeValue(value: any): void {
        this._lastCommittedValue = value;
        const itemToSelect = this.findItemForValue(value);

        if (itemToSelect) {
            this.inputText = this.getItemDisplay(itemToSelect);
        } else if (this.allowCreateNew && typeof value === 'object' && value && this.displayField in value) {
            this.inputText = this.getItemDisplay(value);
        }
        else {
            this.inputText = '';
        }
        this._lastCommittedDisplayText = this.inputText;
        this.filteredItems = [...this.items];
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }


    onInput(value: string): void {
        this.inputText = value;
        this.wasActionTaken = false;
        this.onChange(null);

        // Apply the local filter FIRST so the list updates on the same frame as the keystroke.
        const term = this.normalizeString(value);
        this.filteredItems = term
            ? this.items.filter(item => this.normalizeString(this.getItemDisplay(item)).includes(term))
            : [...this.items];
        this.showResults = true;

        // Notify the parent last — it may update [items] from the outside, but
        // ngOnChanges will re-apply the active term so the result is preserved.
        this.inputValueChanged.emit(value);
    }

    onFocus(): void {
        if (this.isLoading) {
            this.showResults = false;
            return;
        }
        this.wasActionTaken = false;

        const userWasSearching = this.inputText !== this._lastCommittedDisplayText;
        if (userWasSearching || this.filteredItems.length !== this.items.length) {
            this.filteredItems = [...this.items];
        }

        this.showResults = true;
        this.focusEvent.emit();
        this.onTouched();
    }

    onBlur(): void {
        setTimeout(() => {
            if (!this.wasActionTaken) {
                this.inputText = this._lastCommittedDisplayText;
                this.onChange(this._lastCommittedValue);
            }
            this.showResults = false;
            this.wasActionTaken = false;
        }, 200);
    }


    selectItem(item: any, emitEvent: boolean = true): void {
        this.wasActionTaken = true;
        const modelValue = this.valueField && item ? item[this.valueField!] : item;
        this.inputText = this.getItemDisplay(item);

        this._lastCommittedValue = modelValue;
        this._lastCommittedDisplayText = this.inputText;

        this.onChange(modelValue);
        if (emitEvent) {
            this.itemSelected.emit(item);
        }
        this.filteredItems = [];
        this.showResults = false;
    }

    handleCreateNew(): void {
        this.wasActionTaken = true;
        this.showResults = false;

        const newName = this.inputText.trim();
        if (!newName) return;

        this._lastCommittedDisplayText = newName;
        const tempRepresentation = { [this.displayField]: newName };
        this._lastCommittedValue = tempRepresentation;

        this.onChange(tempRepresentation);
        this.createNewRequested.emit(newName);
    }

    getItemDisplay(item: any): string {
        if (!item) return '';
        if (typeof item === 'object' && this.displayField in item) {
            return String(item[this.displayField]);
        }
        if (typeof item === 'string') {
            return item;
        }
        return '';
    }

    private normalizeString(str: string): string {
        if (!str) return '';
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    }

    trackByItem(index: number, item: unknown): unknown {
        if (this.valueField && item && typeof item === 'object') {
            return (item as Record<string, unknown>)[this.valueField] ?? index;
        }
        return index;
    }

    public get lastCommittedValue(): unknown {
        return this._lastCommittedValue;
    }

    get showCreateOption(): boolean {
        if (!this.allowCreateNew || !this.inputText || this.isLoading) {
            return false;
        }
        const trimmedInput = this.normalizeString(this.inputText);
        return !this.items.some(item => this.normalizeString(this.getItemDisplay(item)) === trimmedInput);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target) && this.showResults) {
            if (!this.wasActionTaken) {
                this.inputText = this._lastCommittedDisplayText;
                this.onChange(this._lastCommittedValue);
            }
            this.showResults = false;
            this.wasActionTaken = false;
        }
    }
}
